import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, XCircle, Calendar, Clock, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReservationSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [booking, setBooking] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/reservation');
      return;
    }

    // Poll payment status
    const pollPaymentStatus = async (attempt = 0) => {
      const maxAttempts = 8;
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        setPaymentData(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          
          // Fetch booking details
          const bookingResponse = await axios.get(`${BACKEND_URL}/api/bookings/${response.data.booking_id}`);
          setBooking(bookingResponse.data);
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          // Continue polling
          setPollingCount(attempt + 1);
          setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempt < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, navigate]);

  return (
    <div className="page-reservation-success">
      <section className="success-section">
        <div className="container-success">
          {status === 'checking' && (
            <div className="status-card checking">
              <Loader className="status-icon spin" size={80} />
              <h1>Vérification du paiement...</h1>
              <p>Veuillez patienter pendant que nous confirmons votre paiement</p>
              {pollingCount > 0 && (
                <div className="polling-dots">
                  <span>●</span><span>●</span><span>●</span>
                </div>
              )}
            </div>
          )}

          {status === 'success' && booking && (
            <div className="status-card success">
              <CheckCircle className="status-icon" size={80} />
              <h1>Paiement confirmé !</h1>
              <p className="success-message">
                Votre acompte a été reçu avec succès. Votre réservation est maintenant confirmée.
              </p>

              <div className="booking-summary">
                <h2>Détails de votre réservation</h2>
                
                <div className="summary-grid">
                  <div className="summary-item">
                    <Calendar size={24} />
                    <div>
                      <strong>Date</strong>
                      <p>{new Date(booking.date).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                    </div>
                  </div>

                  <div className="summary-item">
                    <Clock size={24} />
                    <div>
                      <strong>Heure</strong>
                      <p>{booking.time}</p>
                    </div>
                  </div>
                </div>

                <div className="service-info">
                  <h3>{booking.service}</h3>
                  <p className="deposit-info">
                    Acompte versé : <strong>{booking.deposit_amount}€</strong>
                  </p>
                </div>

                <div className="contact-info">
                  <h3>Vos coordonnées</h3>
                  <div className="contact-item">
                    <Mail size={18} />
                    <span>{booking.email}</span>
                  </div>
                  <div className="contact-item">
                    <Phone size={18} />
                    <span>{booking.phone}</span>
                  </div>
                </div>

                <div className="confirmation-note">
                  <p>
                    Un email de confirmation a été envoyé à <strong>{booking.email}</strong>
                  </p>
                  <p className="note">
                    📧 Veuillez vérifier votre boîte de réception (et vos spams si nécessaire)
                  </p>
                </div>
              </div>

              <div className="success-actions">
                <button onClick={() => navigate('/')} className="btn-primary">
                  Retour à l'accueil
                </button>
                <button onClick={() => navigate('/contact')} className="btn-secondary">
                  Nous contacter
                </button>
              </div>
            </div>
          )}

          {status === 'expired' && (
            <div className="status-card expired">
              <XCircle className="status-icon" size={80} />
              <h1>Session expirée</h1>
              <p>La session de paiement a expiré. Veuillez créer une nouvelle réservation.</p>
              <button onClick={() => navigate('/reservation')} className="btn-primary">
                Nouvelle réservation
              </button>
            </div>
          )}

          {status === 'timeout' && (
            <div className="status-card timeout">
              <Clock className="status-icon" size={80} />
              <h1>Délai d'attente dépassé</h1>
              <p>
                Nous n'avons pas pu vérifier votre paiement immédiatement.
                Veuillez consulter votre email pour la confirmation.
              </p>
              <p className="note">
                Si vous avez des questions, n'hésitez pas à nous contacter.
              </p>
              <div className="timeout-actions">
                <button onClick={() => navigate('/')} className="btn-primary">
                  Retour à l'accueil
                </button>
                <button onClick={() => navigate('/contact')} className="btn-secondary">
                  Nous contacter
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="status-card error">
              <XCircle className="status-icon" size={80} />
              <h1>Erreur</h1>
              <p>
                Une erreur s'est produite lors de la vérification de votre paiement.
              </p>
              <p className="note">
                Veuillez nous contacter pour vérifier l'état de votre réservation.
              </p>
              <div className="error-actions">
                <button onClick={() => navigate('/contact')} className="btn-primary">
                  Nous contacter
                </button>
                <button onClick={() => navigate('/reservation')} className="btn-secondary">
                  Nouvelle réservation
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReservationSuccess;
